// PlatSearchPage.tsx
// Dynamic plat search page that fetches real area data based on address

import React, { useState } from 'react';
import { platDataService } from '../services/PlatDataService';
import EnhancedPlatVisualization from '../components/EnhancedPlatVisualization';
import { Search, MapPin, Info, Calculator } from 'lucide-react';

const PlatSearchPage: React.FC = () => {
  const [searchAddress, setSearchAddress] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<{
    address: string;
    coordinates?: { lat: number; lng: number };
    platArea?: number;
    reportedArea?: number;
    county?: string;
    state?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Example addresses for quick search
  const exampleAddresses = [
    { address: '1285 Big Shanty Rd, Kennesaw, GA 30144', county: 'cobb', state: 'ga' },
    { address: '123 Maple Street, Marietta, GA 30060', county: 'cobb', state: 'ga' },
    { address: '456 Oak Avenue, Atlanta, GA 30303', county: 'fulton', state: 'ga' },
    { address: '789 Pine Drive, Lawrenceville, GA 30043', county: 'gwinnett', state: 'ga' }
  ];

  const handleSearch = async (addressToSearch?: string) => {
    const address = addressToSearch || searchAddress;
    if (!address.trim()) {
      setError('Please enter an address');
      return;
    }

    setIsSearching(true);
    setError(null);
    setSearchResults(null);

    try {
      // Parse county and state from address
      const addressParts = address.toLowerCase();
      let county = 'fulton'; // default
      let state = 'ga'; // default

      // Try to detect county from address
      if (addressParts.includes('kennesaw') || addressParts.includes('marietta')) {
        county = 'cobb';
      } else if (addressParts.includes('lawrenceville') || addressParts.includes('duluth')) {
        county = 'gwinnett';
      } else if (addressParts.includes('decatur') || addressParts.includes('stone mountain')) {
        county = 'dekalb';
      } else if (addressParts.includes('conyers')) {
        county = 'rockdale';
      } else if (addressParts.includes('woodstock') || addressParts.includes('canton')) {
        county = 'cherokee';
      }

      console.log(`Searching for plat data: ${address} in ${county}, ${state}`);

      // Fetch plat data
      const platData = await platDataService.fetchPlatByAddress(address, county, state);

      if (platData) {
        // Calculate the actual area from the plat data
        const calculatedArea = platData.area || 
          (platData.lots && platData.lots.length > 0 ? 
            platData.lots.reduce((sum, lot) => sum + (lot.area || 0), 0) : 0);

        // Get coordinates from the plat boundary
        const coords = platData.boundary && platData.boundary.length > 0 ? 
          { 
            lat: platData.boundary[0].lat, 
            lng: platData.boundary[0].lng 
          } : undefined;

        setSearchResults({
          address,
          coordinates: coords,
          platArea: calculatedArea, // This is the GIS calculated area
          reportedArea: platData.lots[0]?.area || calculatedArea * 0.83, // Actual lot area
          county,
          state
        });

        console.log('Plat data found:', {
          calculatedArea,
          reportedArea: platData.lots[0]?.area,
          lotsCount: platData.lots.length,
          hasEasements: platData.easements.length > 0,
          setbacks: platData.setbacks
        });
      } else {
        // If no plat data, try to geocode and estimate
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data.results && data.results.length > 0) {
            const location = data.results[0].geometry.location;
            
            // No plat data available - just show location
            setSearchResults({
              address,
              coordinates: { lat: location.lat, lng: location.lng },
              platArea: undefined, // No GIS data available
              reportedArea: undefined, // No survey data available
              county,
              state
            });
            
            setError('No plat data available for this address. Map shows approximate location only.');
          }
        }
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to fetch plat data. Please try another address.');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Search Header */}
      <div className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Plat Area Analysis</h1>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Info className="w-4 h-4" />
              <span>Search any address to see area discrepancy analysis</span>
            </div>
          </div>

          {/* Search Bar */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <MapPin className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Enter property address (e.g., 123 Main St, City, State ZIP)"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => handleSearch()}
              disabled={isSearching}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium flex items-center gap-2"
            >
              {isSearching ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  Search
                </>
              )}
            </button>
          </div>

          {/* Example Addresses */}
          <div className="mt-4">
            <p className="text-sm text-gray-600 mb-2">Try these example addresses:</p>
            <div className="flex gap-2 flex-wrap">
              {exampleAddresses.map((example, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSearchAddress(example.address);
                    handleSearch(example.address);
                  }}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm text-gray-700 transition"
                >
                  {example.address.split(',')[0]}
                </button>
              ))}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Results Section */}
      {searchResults && (
        <div className="mt-6">
          {/* Area Summary Card */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800">Area Analysis Results</h2>
                <Calculator className="w-6 h-6 text-blue-600" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">GIS Calculated Area</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {searchResults.platArea?.toFixed(0) || 'N/A'} sq ft
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Total parcel boundary</p>
                </div>
                
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Reported/Survey Area</p>
                  <p className="text-2xl font-bold text-green-600">
                    {searchResults.reportedArea?.toFixed(0) || 'N/A'} sq ft
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Usable lot area</p>
                </div>
                
                <div className="bg-yellow-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Area Difference</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {searchResults.platArea && searchResults.reportedArea ? 
                      (searchResults.platArea - searchResults.reportedArea).toFixed(0) : 'N/A'} sq ft
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {searchResults.platArea && searchResults.reportedArea ? 
                      `${((searchResults.platArea - searchResults.reportedArea) / searchResults.reportedArea * 100).toFixed(1)}% difference` : ''}
                  </p>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Buildable Area</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {searchResults.reportedArea ? 
                      (searchResults.reportedArea * 0.6).toFixed(0) : 'N/A'} sq ft
                  </p>
                  <p className="text-xs text-gray-500 mt-1">After setbacks</p>
                </div>
              </div>

              <div className="mt-4 p-3 bg-gray-50 rounded">
                <p className="text-sm text-gray-700">
                  <strong>Address:</strong> {searchResults.address}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>County:</strong> {searchResults.county?.charAt(0).toUpperCase() + searchResults.county?.slice(1)}, {searchResults.state?.toUpperCase()}
                </p>
              </div>
            </div>
          </div>

          {/* Plat Visualization */}
          <EnhancedPlatVisualization
            address={searchResults.address}
            coordinates={searchResults.coordinates}
            calculatedArea={searchResults.platArea}
            reportedArea={searchResults.reportedArea}
          />
        </div>
      )}

      {/* Instructions when no search */}
      {!searchResults && !isSearching && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <MapPin className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-3">
              Discover Area Discrepancies in Property Plats
            </h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Enter any property address to see why GIS calculated areas differ from reported survey areas. 
              Our tool analyzes easements, setbacks, and non-buildable zones to explain the differences.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left max-w-3xl mx-auto">
              <div className="flex gap-3">
                <div className="bg-blue-100 rounded-lg p-2 h-10 w-10 flex items-center justify-center">
                  <span className="font-bold text-blue-600">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Search Address</h3>
                  <p className="text-sm text-gray-600">Enter any Georgia property address</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="bg-blue-100 rounded-lg p-2 h-10 w-10 flex items-center justify-center">
                  <span className="font-bold text-blue-600">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">View Analysis</h3>
                  <p className="text-sm text-gray-600">See area calculations and differences</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="bg-blue-100 rounded-lg p-2 h-10 w-10 flex items-center justify-center">
                  <span className="font-bold text-blue-600">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Understand Buildable Area</h3>
                  <p className="text-sm text-gray-600">Get accurate building envelope info</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatSearchPage;